import * as Highcharts from 'highcharts'
import 'highcharts/highcharts-more'
import 'highcharts/modules/exporting'

import { ActionCardData } from '@playbooks/action-cards-utils/src'
import i18next from 'i18next'

const styles: Record<string, { background: string; textColor: string; gridLineColor: string }> = {
  true: {
    background: '#0a0a0a',
    textColor: 'white',
    gridLineColor: '#333333',
  },
  false: {
    background: 'white',
    textColor: 'black',
    gridLineColor: '#cecece',
  },
}

export function getHighchartOptions(actionCard: ActionCardData, isDark: boolean): Highcharts.Options | null {
  if (!actionCard || !actionCard.useScoringDimensions) {
    return null
  }

  const scoringDimensions = actionCard.scoringDimensions || []

  const scores = scoringDimensions.map(d => d.score || 0)

  const scoresSum = scores.reduce((acc, x) => acc + x, 0)

  const total = Math.round(scoresSum * 100) / 100

  const isDarkKey = String(isDark)
  const { axisNames, axisValues } = scoringDimensions.reduce(
    (acc: { axisNames: string[]; axisValues: number[] }, cur) => {
      acc.axisNames.push(cur.name)
      acc.axisValues.push(cur.actual || 0)
      return acc
    },
    { axisNames: [], axisValues: [] },
  )

  return {
    chart: {
      className: 'spider-chart',
      backgroundColor: styles[isDarkKey].background,
      polar: true,
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: axisNames,
      tickmarkPlacement: 'on',
      gridLineColor: styles[isDarkKey].gridLineColor,
      lineWidth: 0,
      labels: {
        style: {
          color: styles[isDarkKey].textColor,
        },
      },
    },
    yAxis: {
      gridLineInterpolation: 'polygon',
      gridLineColor: styles[isDarkKey].gridLineColor,
      min: 0,
      max: 10,
      tickPositions: [0, 2, 4, 6, 8, 10],
      tickInterval: 2,
      labels: {
        style: {
          color: styles[isDarkKey].textColor,
        },
      },
    },
    tooltip: {
      format: '<span>{key}: <b>{point.y:,.2f}</b></span>',
    },
    series: [
      {
        data: axisValues,
        pointPlacement: 'on',
      },
    ],
    legend: {
      className: 'spider-chart-legend',
      symbolWidth: 0,
      title: {
        style: {
          color: styles[isDarkKey].textColor,
        },
      },
      events: {
        itemClick: () => false,
      },
      labelFormatter: function () {
        return `<b>${i18next.t('chart.totalScoreLabel', 'Weighted score')}: ${total}</b>`
      },
      layout: 'vertical',
      itemStyle: {
        color: styles[isDarkKey].textColor,
      },
    },
    pane: {
      size: '90%',
    },
    colors: ['#FFAAAA', '#D46A6A', '#AA3939', '#801515', '#550000'],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            title: {
              x: 0,
            },
            legend: {
              align: 'center',
              verticalAlign: 'bottom',
              layout: 'horizontal',
            },
            pane: {
              size: '70%',
            },
          },
        },
      ],
    },
    exporting: {
      enabled: false, // to hide menu with exporting actions
      fallbackToExportServer: false,
    },
    credits: {
      enabled: false,
    },
  }
}

export function getHighchartSvg(actionCard: ActionCardData, isDark: boolean = false): string {
  try {
    const options = getHighchartOptions(actionCard, isDark)

    if (!options) {
      return ''
    }

    const chartContainer = document.createElement('div')
    const chart = Highcharts.chart(chartContainer, options)
    const svg = chart.exporting.getSVG() as string

    chart.destroy()
    chartContainer.remove()

    const divElement = document.createElement('div')
    divElement.style.width = '100%'
    divElement.style.display = 'flex'
    divElement.innerHTML = svg ?? ''
    divElement.querySelector('svg')!.style.width = '100%'

    divElement.querySelector('defs')?.remove()
    divElement.querySelector('.highcharts-pane-group')?.remove()
    // The last child of the .highcharts-axis-labels element shows a number of the axis values. It looks like an odd one out
    divElement.querySelector('.highcharts-axis-labels')?.lastChild?.remove()

    return divElement.innerHTML
  } catch (ex) {
    console.error(ex)
    return ''
  }
}

export async function getHighchartAsPng(actionCard: ActionCardData, isDark: boolean = false) {
  try {
    const settings = getHighchartOptions(actionCard, isDark)

    if (!settings) {
      return null
    }

    const chartContainer = document.createElement('div')
    const chart = Highcharts.chart(chartContainer, settings)
    const chartSvg = getHighchartSvg(actionCard, isDark)

    chart.destroy()
    chartContainer.remove()

    const url = URL.createObjectURL(new Blob([chartSvg], { type: 'image/svg+xml' }))

    const img = new Image()

    const promise = new Promise<Blob>((resolve, reject) => {
      // Rejection timeout to prevent hanging in case of issues with chart rendering or loading
      setTimeout(reject, 1000, 'Chart rendering timed out')

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = 600
          canvas.height = 400

          canvas.getContext('2d')!.drawImage(img, 0, 0)
          canvas.toBlob(blob => {
            // canvas.remove()
            img.remove()
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to convert chart to PNG'))
            }
          }, 'image/png')
        } catch (ex) {
          console.error(ex)
          reject(ex)
        }
      }
    })

    img.src = url

    return await promise
  } catch (ex) {
    console.error(ex)
  }
}
